// Utility: videoCompression
// Purpose: Video compression configuration and functions for survey uploads

// Video compression config
export const COMPRESSION_TARGET_SIZE_MB = 5; // Target size in MB
export const COMPRESSION_QUALITY_LEVELS = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1]; // Quality levels to try
export const VIDEO_BITRATE_MULTIPLIER = 1000000; // 1 Mbps = 1,000,000 bps
export const DEFAULT_VIDEO_BITRATE = 2.5 * VIDEO_BITRATE_MULTIPLIER; // Default 2.5 Mbps
export const LOW_VIDEO_BITRATE = 1 * VIDEO_BITRATE_MULTIPLIER; // 1 Mbps for larger videos

// Function to compress video using the MediaRecorder API
export const compressVideo = (file, setCompressionProgress) => {
  return new Promise((resolve, reject) => {
    console.log('Starting compression for file:', file.name, file.size);
    let videoElement = null;
    let progressInterval = null;
    
    // Cleanup function to release resources
    const cleanup = () => {
      console.log('Cleaning up compression resources');
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      
      if (videoElement && videoElement.src) {
        videoElement.pause();
        URL.revokeObjectURL(videoElement.src);
        videoElement = null;
      }
    };
    
    // Skip compression for GIFs and small files
    if (file.type === 'image/gif' || file.size < 1000000) { // Less than 1MB
      console.log('File is small enough or is a GIF, skipping compression');
      setCompressionProgress(100);
      resolve({ compressedFile: file, originalSize: file.size, compressedSize: file.size });
      return;
    }

    // Create a video element for the source video
    videoElement = document.createElement('video');
    videoElement.preload = 'metadata';
    videoElement.src = URL.createObjectURL(file);
    videoElement.muted = true; // Mute for preview but audio will be preserved
    
    // Set up initial progress
    setCompressionProgress(5);
    
    videoElement.onloadedmetadata = () => {
      const originalDuration = videoElement.duration;
      const originalSize = file.size;
      console.log(`Original video: ${originalSize / 1024 / 1024} MB, Duration: ${originalDuration}s`);
      
      // Prepare the MediaRecorder with audio-preserving options
      try {
        // Calculate target bitrate based on desired file size
        // Formula: bitrate = (target size in bits) / duration in seconds
        const targetSizeInBits = COMPRESSION_TARGET_SIZE_MB * 8 * 1024 * 1024;
        const targetBitrate = Math.min(
          targetSizeInBits / originalDuration,
          // Use lower bitrate for longer videos
          originalDuration > 60 ? LOW_VIDEO_BITRATE : DEFAULT_VIDEO_BITRATE
        );
        
        // Set up progress tracking
        setCompressionProgress(15);
        
        // Create MediaRecorder options
        const options = {
          mimeType: 'video/webm;codecs=vp9',
          videoBitsPerSecond: targetBitrate,
          audioBitsPerSecond: 128000 // 128kbps for audio
        };
        
        // Fallback options if VP9 isn't supported
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'video/webm;codecs=vp8';
        }
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'video/webm';
        }
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = '';
        }
        
        // Play the video now that we've set up everything
        videoElement.play();
        
        // Record directly from the video element
        const mediaStream = videoElement.captureStream();
        const mediaRecorder = new MediaRecorder(mediaStream, options);
        const chunks = [];
        
        // Start recording
        mediaRecorder.start();
        console.log('Started recording with audio preservation');
        
        // Setup progress tracking
        progressInterval = setInterval(() => {
          if (videoElement.currentTime > 0 && !videoElement.paused && !videoElement.ended) {
            // Calculate progress based on time elapsed
            const progress = Math.min(Math.round((videoElement.currentTime / originalDuration) * 80) + 15, 95);
            setCompressionProgress(progress);
          }
        }, 100);
        
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          // Run cleanup function to release resources
          cleanup();
          
          // Show final processing step
          setCompressionProgress(95);
          
          // Create a Blob from the recorded chunks
          const blob = new Blob(chunks, { type: 'video/webm' });
          const compressedSize = blob.size;
          
          // Complete the progress bar
          setCompressionProgress(100);
          
          // Log compression results
          console.log(`Compressed video (with audio): ${compressedSize / 1024 / 1024} MB, Reduction: ${((1 - (compressedSize / originalSize)) * 100).toFixed(2)}%`);
          
          // Check if the compressed video is not too much larger than the original
          // This can happen with already optimized videos
          if (compressedSize > originalSize * 1.1) {
            console.log('Compressed version is larger, using original video');
            resolve({
              compressedFile: file,
              originalSize,
              compressedSize: originalSize
            });
            return;
          }
          
          // Convert the blob to a File object
          const compressedFile = new File([blob], file.name, {
            type: 'video/webm',
            lastModified: new Date().getTime()
          });
          
          resolve({
            compressedFile, 
            originalSize, 
            compressedSize
          });
        };
        
        // Stop recording when video ends
        videoElement.onended = () => {
          console.log('Video playback ended, stopping recorder');
          if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
          }
        };
        
      } catch (err) {
        console.error('Error setting up media recorder:', err);
        cleanup();
        
        // If compression fails, use the original video
        console.log('Using original video due to compression setup error');
        resolve({
          compressedFile: file,
          originalSize: file.size,
          compressedSize: file.size
        });
      }
    };
    
    videoElement.onerror = (e) => {
      console.error('Error loading video metadata:', e);
      cleanup();
      reject(new Error('Failed to load video metadata'));
    };
  });
};


